/**
 * Fleet data access: TanStack Query hooks over the typed client. Other modules may use these
 * hooks (a module's "service functions" on the web side), never fleet's pages.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type {
  DeviceCreate,
  DevicePatch,
  DeviceSort,
  DimensionCreate,
  DriverCreate,
  DriverPatch,
  DriverSort,
  GroupCreate,
  GroupingOut,
  VehicleCreate,
  VehiclePatch,
  VehicleSort,
} from '@/lib/api/types';

export const fleetKeys = {
  all: ['fleet'] as const,
  vehicles: (p: object) => ['fleet', 'vehicles', p] as const,
  vehicle: (id: string) => ['fleet', 'vehicle', id] as const,
  drivers: (p: object) => ['fleet', 'drivers', p] as const,
  driver: (id: string) => ['fleet', 'driver', id] as const,
  devices: (p: object) => ['fleet', 'devices', p] as const,
  device: (id: string) => ['fleet', 'device', id] as const,
  profiles: ['fleet', 'profiles'] as const,
  groupings: ['fleet', 'groupings'] as const,
  members: (groupId: string) => ['fleet', 'members', groupId] as const,
};

const PAGE = 200;

/** The filtered total of a paged list (from its first page): exact, or an estimate above 10,000. */
export function listTotal(pages: { total?: number | null; total_estimated?: boolean }[] | undefined): { total: number; estimated: boolean } | null {
  const first = pages?.[0];
  if (!first || first.total == null) return null;
  return { total: first.total, estimated: !!first.total_estimated };
}

export interface VehicleQuery {
  project?: string[];
  group?: string[];
  status?: string;
  q?: string;
  vehicle_type?: string;
  /** Server-side sort: `field` ascending, `-field` descending (default: name). */
  sort?: VehicleSort;
  /** Page size (default 200); a count-only view asks for 1. */
  limit?: number;
}

export function useVehicles(p: VehicleQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: fleetKeys.vehicles(p),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET('/api/fleet/vehicles', { params: { query: { ...p, status: p.status ?? 'active', cursor: pageParam, limit: p.limit ?? PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: fleetKeys.vehicle(id),
    queryFn: () => unwrap(api.GET('/api/fleet/vehicles/{vehicle_id}', { params: { path: { vehicle_id: id } } })),
  });
}

export interface DriverQuery {
  project?: string[];
  group?: string[];
  status?: string;
  q?: string;
  /** Server-side sort: `field` ascending, `-field` descending (default: name). */
  sort?: DriverSort;
  limit?: number;
}

export function useDrivers(p: DriverQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: fleetKeys.drivers(p),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET('/api/fleet/drivers', { params: { query: { ...p, status: p.status ?? 'active', cursor: pageParam, limit: p.limit ?? PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDriver(id: string) {
  return useQuery({
    queryKey: fleetKeys.driver(id),
    queryFn: () => unwrap(api.GET('/api/fleet/drivers/{driver_id}', { params: { path: { driver_id: id } } })),
  });
}

export interface DeviceQuery {
  project?: string[];
  profile?: string;
  status?: string;
  linked?: boolean;
  q?: string;
  /** Server-side sort: `field` ascending, `-field` descending (default: identifier). */
  sort?: DeviceSort;
  limit?: number;
}

export function useDevices(p: DeviceQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: fleetKeys.devices(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/fleet/devices', { params: { query: { ...p, cursor: pageParam, limit: p.limit ?? PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDevice(id: string) {
  return useQuery({
    queryKey: fleetKeys.device(id),
    queryFn: () => unwrap(api.GET('/api/fleet/devices/{device_id}', { params: { path: { device_id: id } } })),
  });
}

export function useProfiles(enabled = true) {
  return useQuery({ queryKey: fleetKeys.profiles, queryFn: () => unwrap(api.GET('/api/fleet/profiles')), staleTime: 10 * 60_000, enabled });
}

/** A grouping (group dimension) with its active groups. */
export type Grouping = GroupingOut;

export function useGroupings(enabled = true) {
  return useQuery({
    queryKey: fleetKeys.groupings,
    queryFn: () => unwrap(api.GET('/api/fleet/groupings')),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** A group's current members, newest first, a page at a time (with the total on every page). */
export function useGroupMembers(groupId: string | undefined) {
  return useInfiniteQuery({
    queryKey: fleetKeys.members(groupId ?? ''),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET('/api/fleet/groups/{group_id}/members', { params: { path: { group_id: groupId ?? '' }, query: { cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!groupId,
  });
}

/* Every write returns the action result (ActionOut: message, data, links). */

/** Add a vehicle (fleet.create_vehicle); the new id is `data.vehicle_id`. */
export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VehicleCreate) => unwrap(api.POST('/api/fleet/vehicles', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet', 'vehicles'] }),
  });
}

/** Add a driver (fleet.create_driver); the new id is `data.driver_id`. */
export function useCreateDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DriverCreate) => unwrap(api.POST('/api/fleet/drivers', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet', 'drivers'] }),
  });
}

/** Register a device, optionally fitted to a vehicle at once (fleet.register_device); the new id is `data.device_id`. */
export function useRegisterDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeviceCreate) => unwrap(api.POST('/api/fleet/devices', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}

export function useUpdateVehicle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VehiclePatch) => unwrap(api.PATCH('/api/fleet/vehicles/{vehicle_id}', { params: { path: { vehicle_id: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}

export function useUpdateDriver(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DriverPatch) => unwrap(api.PATCH('/api/fleet/drivers/{driver_id}', { params: { path: { driver_id: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}

export function useUpdateDevice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DevicePatch) => unwrap(api.PATCH('/api/fleet/devices/{device_id}', { params: { path: { device_id: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}

/** Fit a device to a vehicle, or remove it (vehicleId null). */
export function useLinkDevice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vehicleId: string | null) =>
      unwrap(api.POST('/api/fleet/devices/{device_id}/link', { params: { path: { device_id: id } }, body: { vehicle_id: vehicleId } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}

export function useCreateGrouping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DimensionCreate) => unwrap(api.POST('/api/fleet/groupings', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.groupings }),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GroupCreate) => unwrap(api.POST('/api/fleet/groups', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.groupings }),
  });
}

export function useSetGroupMembers(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (change: { add?: string[]; remove?: string[] }) =>
      unwrap(
        api.POST('/api/fleet/groups/{group_id}/members', {
          params: { path: { group_id: groupId } },
          body: { add: change.add ?? [], remove: change.remove ?? [] },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: fleetKeys.all }),
  });
}
